import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "../../utils/auth";
import { canViewAnalytics } from "../../utils/roles";
import {
  downloadDeliveredCustomersCsv,
  fetchFincartAnalyticsFilters,
  fetchFincartAnalyticsOrders,
  fetchFincartAnalyticsSummary,
} from "../../api/FincartAnalyticsAPIs";
import {
  FINCART_DATE_PRESETS,
  formatDateTime,
  resolveFincartDateRange,
} from "../../utils/analyticsDates";
import { shippingStateLabel } from "../../Components/orders/ShippingTimeline";
import { ChartBox, EmptyState, KpiCard, LoadingBlock, SectionCard } from "./AnalyticsUi";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChevronLeft, Download, Truck } from "lucide-react";

const STATE_FILTERS = [
  { value: "", label: "كل الحالات" },
  { value: "PENDING", label: "قيد الانتظار" },
  { value: "IN_TRANSIT_GROUP", label: "قيد الشحن" },
  { value: "OUT_FOR_DELIVERY", label: "خرج للتسليم" },
  { value: "FAILED_ATTEMPT", label: "محاولة فاشلة" },
  { value: "DELIVERED", label: "تم التسليم" },
  { value: "RETURN_IN_PROGRESS", label: "جاري الإرجاع" },
  { value: "RETURNED", label: "مرتجع" },
  { value: "CANCELLED", label: "ملغي" },
  { value: "PROBLEMATIC", label: "مشكلة" },
  { value: "UNSYNCED", label: "غير متزامن" },
];

const SLICE_COLORS = {
  DELIVERED: "#059669",
  IN_TRANSIT: "#0284c7",
  RETURNED: "#e11d48",
  FAILED_ATTEMPT: "#d97706",
  CANCELLED: "#64748b",
  RETURN_IN_PROGRESS: "#ea580c",
  PENDING: "#94a3b8",
  PROBLEMATIC: "#dc2626",
  UNSYNCED: "#a1a1aa",
  OTHER: "#78716c",
};

const defaultDraft = () => ({
  preset: "thisMonth",
  customFrom: "",
  customTo: "",
  shippingState: "",
  governorate: "",
  city: "",
  carrier: "",
  paymentMethod: "",
  search: "",
});

const draftToFilters = (draft) => {
  const range = resolveFincartDateRange(draft.preset, draft.customFrom, draft.customTo);
  return {
    startDate: range.startDate,
    endDate: range.endDate,
    shippingState: draft.shippingState || undefined,
    governorate: draft.governorate || undefined,
    city: draft.city || undefined,
    carrier: draft.carrier || undefined,
    paymentMethod: draft.paymentMethod || undefined,
    search: draft.search.trim() || undefined,
  };
};

const percentText = (value) =>
  value == null || value === ""
    ? "—"
    : `${Number(value).toLocaleString("ar-EG", { maximumFractionDigits: 1 })}%`;

const daysText = (value) =>
  value == null ? "—" : `${Number(value).toLocaleString("ar-EG", { maximumFractionDigits: 1 })} يوم`;

export default function FincartAnalyticsPage() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [draft, setDraft] = useState(defaultDraft);
  const [applied, setApplied] = useState(() => draftToFilters(defaultDraft()));
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [showAllGovernorates, setShowAllGovernorates] = useState(false);

  React.useEffect(() => {
    if (!authState.token) {
      navigate("/login");
      return;
    }
    if (!canViewAnalytics(authState.role)) navigate("/dashboard");
  }, [authState, navigate]);

  const filtersQuery = useQuery({
    queryKey: ["fincart-analytics-filters"],
    queryFn: fetchFincartAnalyticsFilters,
    staleTime: 10 * 60 * 1000,
  });

  const summaryQuery = useQuery({
    queryKey: ["fincart-analytics-summary", applied],
    queryFn: () => fetchFincartAnalyticsSummary(applied),
    placeholderData: keepPreviousData,
  });

  const ordersQuery = useQuery({
    queryKey: ["fincart-analytics-orders", applied, page],
    queryFn: () => fetchFincartAnalyticsOrders(applied, page, 25),
    placeholderData: keepPreviousData,
  });

  const places = filtersQuery.data?.places || [];
  const governorates = useMemo(
    () => [...new Set(places.map((p) => p.governorate).filter(Boolean))],
    [places]
  );
  const cities = useMemo(
    () =>
      places
        .filter((p) => !draft.governorate || p.governorate === draft.governorate)
        .map((p) => p.city)
        .filter(Boolean)
        .filter((city, index, all) => all.indexOf(city) === index),
    [places, draft.governorate]
  );
  const carriers = filtersQuery.data?.carriers || [];

  const summary = summaryQuery.data;
  const kpis = summary?.kpis;
  const governorateRows = summary?.governorates || [];
  const visibleGovernorates = showAllGovernorates ? governorateRows : governorateRows.slice(0, 8);
  const pageCount = Math.max(1, Math.ceil((ordersQuery.data?.totalCount || 0) / 25));

  const apply = () => {
    setApplied(draftToFilters(draft));
    setPage(0);
    setShowAllGovernorates(false);
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportError("");
    try {
      await downloadDeliveredCustomersCsv(applied);
    } catch (err) {
      setExportError(err.message || "فشل التصدير");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-2"
            >
              <ChevronLeft size={16} />
              لوحة التحكم
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Truck size={26} className="text-cyan-700" />
              تحليلات Fincart
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-3xl">
              الأرقام حسب تاريخ إنشاء الطلب، وللأوردرات التي تم إرسالها إلى Fincart فقط. الحالة هي آخر حالة شحن متزامنة، مش مجرد علامة الإرسال.
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-bold hover:bg-emerald-700 disabled:opacity-60"
            title="عملاء تم تسليم طلباتهم داخل التاريخ وباقي الفلاتر، بدون تكرار رقم الهاتف"
          >
            <Download size={16} />
            {exporting ? "جاري التصدير..." : "تصدير عملاء التسليم"}
          </button>
        </div>
        {exportError && <div className="text-sm text-red-600">{exportError}</div>}

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {FINCART_DATE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setDraft((prev) => ({ ...prev, preset: preset.id }))}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                  draft.preset === preset.id
                    ? "bg-cyan-700 text-white border-cyan-700"
                    : "bg-white text-slate-600 border-slate-200"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {draft.preset === "custom" && (
            <div className="flex flex-wrap gap-3">
              <label className="text-sm text-slate-600">
                من
                <input
                  type="date"
                  value={draft.customFrom}
                  onChange={(e) => setDraft((prev) => ({ ...prev, customFrom: e.target.value }))}
                  className="mt-1 block rounded-lg border border-slate-200 px-3 py-2"
                />
              </label>
              <label className="text-sm text-slate-600">
                إلى
                <input
                  type="date"
                  value={draft.customTo}
                  onChange={(e) => setDraft((prev) => ({ ...prev, customTo: e.target.value }))}
                  className="mt-1 block rounded-lg border border-slate-200 px-3 py-2"
                />
              </label>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <FilterSelect
              label="حالة الشحن"
              value={draft.shippingState}
              onChange={(shippingState) => setDraft((prev) => ({ ...prev, shippingState }))}
              options={STATE_FILTERS}
            />
            <FilterSelect
              label="المحافظة"
              value={draft.governorate}
              onChange={(governorate) => setDraft((prev) => ({ ...prev, governorate, city: "" }))}
              options={[{ value: "", label: "كل المحافظات" }, ...governorates.map((name) => ({ value: name, label: name }))]}
            />
            <FilterSelect
              label="المنطقة"
              value={draft.city}
              onChange={(city) => setDraft((prev) => ({ ...prev, city }))}
              options={[{ value: "", label: "كل المناطق" }, ...cities.map((name) => ({ value: name, label: name }))]}
            />
            <FilterSelect
              label="شركة الشحن"
              value={draft.carrier}
              onChange={(carrier) => setDraft((prev) => ({ ...prev, carrier }))}
              options={[
                { value: "", label: "كل الشركات" },
                { value: "غير محدد", label: "غير محدد" },
                ...carriers.map((name) => ({ value: name, label: name })),
              ]}
            />
            <FilterSelect
              label="الدفع"
              value={draft.paymentMethod}
              onChange={(paymentMethod) => setDraft((prev) => ({ ...prev, paymentMethod }))}
              options={[
                { value: "", label: "الكل" },
                { value: "COD", label: "كاش عند الاستلام" },
                { value: "Online", label: "أونلاين" },
              ]}
            />
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm text-slate-600 flex-1 min-w-[220px]">
              بحث برقم الطلب أو الهاتف أو التتبع
              <input
                value={draft.search}
                onChange={(e) => setDraft((prev) => ({ ...prev, search: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <button
              type="button"
              onClick={apply}
              className="rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-bold"
            >
              تطبيق
            </button>
          </div>
        </section>

        {summaryQuery.isLoading && <LoadingBlock />}
        {summaryQuery.error && (
          <div className="text-sm text-red-600">{summaryQuery.error.message}</div>
        )}

        {kpis && kpis.totalOrders === 0 && (
          <EmptyState message="لا توجد أوردرات مرسلة إلى Fincart في هذه الفترة." />
        )}

        {kpis && kpis.totalOrders > 0 && (
          <>
            {(kpis.unsynced > 0 || kpis.missingShippingId > 0) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm p-3">
                {kpis.unsynced > 0 && (
                  <div>{kpis.unsynced.toLocaleString("ar-EG")} أوردر اتبعت لـ Fincart ولسه مالهاش حالة شحن متزامنة. مش محسوبين تسليم أو فشل.</div>
                )}
                {kpis.missingShippingId > 0 && (
                  <div>{kpis.missingShippingId.toLocaleString("ar-EG")} أوردر من غير رقم شحن Fincart محفوظ. المزامنة تقدر ترجّعه.</div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <KpiCard title="أوردرات Fincart" value={kpis.totalOrders.toLocaleString("ar-EG")} accent="cyan" tip="كل الأوردرات المرسلة إلى Fincart داخل الفترة." />
              <KpiCard title="تم التسليم" value={kpis.delivered.toLocaleString("ar-EG")} accent="emerald" />
              <KpiCard title="قيد الشحن" value={kpis.inTransit.toLocaleString("ar-EG")} subtitle={`${kpis.outForDelivery.toLocaleString("ar-EG")} خرج للتسليم`} accent="sky" tip="قيد الشحن يشمل خرج للتسليم." />
              <KpiCard title="مرتجع" value={kpis.returned.toLocaleString("ar-EG")} accent="rose" />
              <KpiCard title="محاولة فاشلة" value={kpis.failedAttempt.toLocaleString("ar-EG")} accent="amber" tip="حالة حالية. المحاولة الفاشلة لسه مش مرتجع." />
              <KpiCard title="ملغي" value={kpis.cancelled.toLocaleString("ar-EG")} accent="violet" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KpiCard title="معدل التسليم" value={percentText(kpis.deliveryRate)} subtitle={`${kpis.finished.toLocaleString("ar-EG")} شحنة منتهية`} accent="emerald" tip="تم التسليم ÷ (تم التسليم + مرتجع). قيد الشحن والانتظار والمحاولة الفاشلة والملغي وغير المتزامن خارج النسبة." />
              <KpiCard title="معدل الإرجاع" value={percentText(kpis.returnRate)} accent="rose" tip="مرتجع ÷ (تم التسليم + مرتجع). نفس مقام معدل التسليم." />
              <KpiCard title="معدل المحاولات الفاشلة" value={percentText(kpis.failedAttemptRate)} subtitle={`${kpis.synced.toLocaleString("ar-EG")} أوردر بحالة معروفة`} accent="amber" tip="أوردرات حالتها الحالية محاولة فاشلة ÷ الأوردرات التي وصلت لها حالة من Fincart." />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <SectionCard title="نتيجة الشحن">
                {(summary.outcomes || []).length === 0 ? (
                  <EmptyState />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <ChartBox className="h-64">
                      <PieChart>
                        <Pie data={summary.outcomes} dataKey="count" nameKey="label" innerRadius={58} outerRadius={88} paddingAngle={2}>
                          {summary.outcomes.map((slice) => (
                            <Cell key={slice.key} fill={SLICE_COLORS[slice.key] || "#64748b"} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name, item) => [`${value} (${percentText(item?.payload?.percent)})`, name]} />
                      </PieChart>
                    </ChartBox>
                    <ul className="space-y-2 text-sm">
                      {summary.outcomes.map((slice) => (
                        <li key={slice.key} className="flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-2 text-slate-700">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: SLICE_COLORS[slice.key] || "#64748b" }} />
                            {slice.label}
                          </span>
                          <span className="font-bold text-slate-900">
                            {slice.count.toLocaleString("ar-EG")} ({percentText(slice.percent)})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </SectionCard>

              <SectionCard title="زمن التسليم">
                <p className="text-xs text-slate-500 mb-3">
                  من أول حدث شحن على Fincart حتى حدث التسليم. الأوردرات الناقص فيها أحد التوقيتين مش داخلة في المتوسط.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <MiniStat label="المتوسط" value={daysText(summary.deliveryTime?.averageDays)} />
                  <MiniStat label="الوسيط" value={daysText(summary.deliveryTime?.medianDays)} />
                  <MiniStat label="الأسرع" value={daysText(summary.deliveryTime?.fastestDays)} />
                  <MiniStat label="الأبطأ" value={daysText(summary.deliveryTime?.slowestDays)} />
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  محسوب من {summary.deliveryTime?.sampleSize?.toLocaleString("ar-EG") || 0} أوردر مسلّم.
                  {summary.deliveryTime?.deliveredWithoutTimestamps > 0 &&
                    ` ${summary.deliveryTime.deliveredWithoutTimestamps.toLocaleString("ar-EG")} مسلّم بدون توقيت صالح.`}
                </p>
              </SectionCard>
            </div>

            <SectionCard title="الأداء عبر الوقت" action={<span className="text-xs text-slate-500">تجميع {summary.granularity === "month" ? "شهري" : summary.granularity === "week" ? "أسبوعي" : "يومي"} حسب تاريخ الطلب</span>}>
              <ChartBox>
                <ComposedChart data={summary.trend || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="orders" name="أوردرات" fill="#0891b2" radius={[4, 4, 0, 0]} />
                  <Line dataKey="delivered" name="تم التسليم" stroke="#059669" strokeWidth={2} dot={false} />
                  <Line dataKey="returned" name="مرتجع" stroke="#e11d48" strokeWidth={2} dot={false} />
                  <Line dataKey="failedAttempt" name="محاولة فاشلة" stroke="#d97706" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ChartBox>
            </SectionCard>

            <SectionCard title="المحافظات">
              <GroupTable rows={visibleGovernorates} />
              {governorateRows.length > 8 && (
                <button
                  type="button"
                  onClick={() => setShowAllGovernorates((open) => !open)}
                  className="mt-3 text-sm font-bold text-cyan-800"
                >
                  {showAllGovernorates ? "عرض أقل" : `عرض الكل (${governorateRows.length})`}
                </button>
              )}
            </SectionCard>

            <SectionCard title="شركات الشحن">
              <p className="text-xs text-slate-500 mb-3">
                اسم الشركة بييجي من Fincart وقت المزامنة. الأوردرات اللي لسه متسجلهاش شركة بتظهر كـ غير محدد ومش بتتحسب على شركة تانية.
                {kpis.withCarrier < kpis.totalOrders &&
                  ` معروف لـ ${kpis.withCarrier.toLocaleString("ar-EG")} من ${kpis.totalOrders.toLocaleString("ar-EG")}.`}
              </p>
              <GroupTable rows={summary.carriers || []} />
            </SectionCard>
          </>
        )}

        <SectionCard title="طلبات Fincart">
          {ordersQuery.isLoading && <LoadingBlock />}
          {ordersQuery.error && <div className="text-sm text-red-600">{ordersQuery.error.message}</div>}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-right">
              <thead className="text-xs text-slate-500 border-b">
                <tr>
                  {["الطلب", "العميل", "المحافظة", "المنطقة", "الشركة", "الحالة", "التتبع", "تاريخ الطلب", "أول حدث شحن", "التسليم", "المدة"].map((heading) => (
                    <th key={heading} className="py-2 px-2 font-semibold whitespace-nowrap">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(ordersQuery.data?.items || []).map((row) => (
                  <tr key={row.orderId} className="border-b border-slate-100">
                    <td className="py-2 px-2 font-bold">#{row.orderId}</td>
                    <td className="py-2 px-2">
                      <div>{row.customerName || "—"}</div>
                      <div className="text-xs text-slate-500" dir="ltr">{row.phoneNumber || ""}</div>
                    </td>
                    <td className="py-2 px-2">{row.governorate || "—"}</td>
                    <td className="py-2 px-2">{row.city || "—"}</td>
                    <td className="py-2 px-2">{row.carrier || "غير محدد"}</td>
                    <td className="py-2 px-2">
                      <div>{shippingStateLabel(row.shippingState)}</div>
                      {row.shippingSubStatus && <div className="text-xs text-slate-500">{row.shippingSubStatus}</div>}
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">{row.trackingNumber || "—"}</td>
                    <td className="py-2 px-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="py-2 px-2 whitespace-nowrap">{formatDateTime(row.shippedAt)}</td>
                    <td className="py-2 px-2 whitespace-nowrap">{formatDateTime(row.deliveredAt)}</td>
                    <td className="py-2 px-2">{daysText(row.deliveryDays)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(ordersQuery.data?.items || []).length === 0 && !ordersQuery.isLoading && (
            <p className="text-sm text-slate-500 mt-3">لا توجد طلبات مطابقة.</p>
          )}
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-slate-500">{(ordersQuery.data?.totalCount || 0).toLocaleString("ar-EG")} طلب</span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 py-1 rounded-lg border disabled:opacity-40">السابق</button>
              <span className="px-2 py-1">{page + 1} / {pageCount}</span>
              <button type="button" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded-lg border disabled:opacity-40">التالي</button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="text-sm text-slate-600">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
      >
        {options.map((option) => (
          <option key={option.value || "all"} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function GroupTable({ rows }) {
  if (!rows?.length) return <EmptyState />;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm text-right">
        <thead className="text-xs text-slate-500 border-b">
          <tr>
            {["الاسم", "أوردرات", "تسليم", "شحن", "مرتجع", "فشل", "ملغي", "غير متزامن", "معدل التسليم", "معدل الإرجاع"].map((heading) => (
              <th key={heading} className="py-2 px-2 font-semibold whitespace-nowrap">{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="border-b border-slate-100">
              <td className="py-2 px-2 font-semibold">{row.name}</td>
              <td className="py-2 px-2">{row.orders.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{row.delivered.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{row.inTransit.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{row.returned.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{row.failedAttempt.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{row.cancelled.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{row.unsynced.toLocaleString("ar-EG")}</td>
              <td className="py-2 px-2">{percentText(row.deliveryRate)}</td>
              <td className="py-2 px-2">{percentText(row.returnRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
